// REACT
import { useEffect, useState } from 'react';

// YARN
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Box, Tab, Tabs, ThemeProvider } from '@mui/material';

// SERVICES
import {
  getCultureGroupList,
  getCultures as getCooperativeCultures,
  getStructureNonReturnCultureGroupList,
} from 'services/API/Cooperative';
import './cultureGroup.scss';

// INTERFACES
import { ICulture } from 'components/generics/Interface/ICulture';
import { IErrorResponse } from 'components/generics/Interface/Commons/IErrorResponse';

// THEMES
import { ICultureGroup, INonReturnCultureGroup } from 'components/generics/Interface/ICultureGroup';
import MixedFarmingCultureGroup from './MixedFarmingCultureGroup';
import NonReturnCultureGroup from './NonReturnCultureGroup';
import {
  getSecteurInformations,
  getCultures as getSectorCultures,
  getSectorNonReturnCultureGroupList,
} from 'services/API/Secteur';
import { useNavigate } from 'react-router-dom';
import {
  getCulturesByExploitationId,
  getExploitationNonReturnCultureGroupList,
} from 'services/API/Exploitation';
import { sendEvent } from 'utils/Event';
import { tabsTheme } from 'assets/styles/themes/tabs_mui';

type CultureGroupParams = {
  ownerId: number;
  from: 'cooperatives' | 'sectors' | 'exploitations';
  setExploitation: Function;
};

const CultureGroup = ({ ownerId, from, setExploitation }: CultureGroupParams) => {
  const [cultures, setCultures] = useState<ICulture[]>([]);
  const [mixedFarmingCultureGroup, setMixedFarmingCultureGroup] = useState<ICultureGroup[]>([]);
  const [nonReturnCultureGroup, setNonReturnCultureGroup] = useState<INonReturnCultureGroup[]>([]);
  const [value, setValue] = useState(0);
  const [localOwnerId, setLocalOwnerId] = useState(ownerId);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    switch (from) {
      case 'cooperatives':
        // loadCulturesData();
        break;
      case 'sectors':
        if (!ownerId) {
          if (localStorage.getItem('sector')) {
            getSecteurInformations(localStorage.getItem('sector'))
              .then((resp: any) => setLocalOwnerId(resp.data.data.id))
              .catch((err: any) => toast.error(err.data));
          } else {
            toast.info(t('message.selectSector'));
            navigate('/sector');
          }
        }
        break;
      case 'exploitations':
        if (!ownerId) {
          if (localStorage.getItem('exploitation')) {
            setExploitation(false, localStorage.getItem('exploitation'))
              .then((resp: any) => setLocalOwnerId(resp.data.data.id))
              .catch((err: any) => toast.error(err.data));
          } else {
            toast.info(t('message.selectExploitation'));
            navigate('/exploitation');
          }
          break;
        }
    }

    // Dismount
    return () => {
      sendEvent('cancelHttpRequest');
    };
  }, []);

  useEffect(() => {
    if (localOwnerId) {
      loadCulturesData();
      loadNonReturnCultureGroupData();
      if (from === 'cooperatives') {
        loadMixedFarmingGroupData();
      }
    }
  }, [localOwnerId]);

  const loadCulturesData = async () => {
    try {
      let results;

      switch (from) {
        case 'cooperatives':
          results = await getCooperativeCultures(localOwnerId, false);
          break;
        case 'sectors':
          results = await getSectorCultures(false, localOwnerId);
          break;
        case 'exploitations':
          results = await getCulturesByExploitationId(localOwnerId, false);
          break;
      }

      setCultures(results?.data?.data);
    } catch (err) {
      const apiError = err as IErrorResponse;
      toast.error(apiError.message);
    }
  };

  const loadMixedFarmingGroupData = async () => {
    try {
      const results = await getCultureGroupList(localOwnerId);
      setMixedFarmingCultureGroup(results.data.data);
    } catch (err) {
      const apiError = err as IErrorResponse;
      toast.error(apiError.message);
    }
  };

  const loadNonReturnCultureGroupData = async () => {
    try {
      let results;

      switch (from) {
        case 'cooperatives':
          results = await getStructureNonReturnCultureGroupList(localOwnerId);
          break;
        case 'sectors':
          results = await getSectorNonReturnCultureGroupList(localOwnerId);
          break;
        case 'exploitations':
          results = await getExploitationNonReturnCultureGroupList(localOwnerId);
          break;
      }
      setNonReturnCultureGroup(results?.data?.data);
    } catch (err) {
      const apiError = err as IErrorResponse;
      toast.error(apiError.message);
    }
  };

  interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
  }

  const CustomTabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  };

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return from === 'cooperatives' ? (
    <div className="section">
      <div className="title_and_tabs">
        <h1 className="title_section_without_media_query title_section_culture">
          {t('title.cultureGroupsList')}
        </h1>
        <ThemeProvider theme={tabsTheme}>
          <Tabs
            value={value}
            onChange={handleChange}
            sx={{
              minWidth: 'auto',
              width: 'fit-content',
            }}
          >
            <Tab label={t('tab.bioConv')} {...a11yProps(0)} />
            <Tab label={t('tab.nonReturn')} {...a11yProps(1)} />
          </Tabs>
        </ThemeProvider>
      </div>

      <CustomTabPanel value={value} index={0}>
        <MixedFarmingCultureGroup
          ownerId={localOwnerId}
          cultures={cultures}
          mixedFarmingCultureGroup={mixedFarmingCultureGroup}
          loadMixedFarmingGroupData={loadMixedFarmingGroupData}
        />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <NonReturnCultureGroup
          ownerId={localOwnerId}
          cultures={cultures}
          nonReturnCultureGroup={nonReturnCultureGroup}
          loadNonReturnCultureGroupData={loadNonReturnCultureGroupData}
          from={from}
        />
      </CustomTabPanel>
    </div>
  ) : (
    <div className="section">
      {from !== 'exploitations' && (
        <h1 className="title_section_without_media_query title_section_culture title_rotate_effects">
          {t('title.nonReturnGroupsList')}
        </h1>
      )}
      <NonReturnCultureGroup
        ownerId={localOwnerId}
        cultures={cultures}
        nonReturnCultureGroup={nonReturnCultureGroup}
        loadNonReturnCultureGroupData={loadNonReturnCultureGroupData}
        from={from}
      />
    </div>
  );
};

export default CultureGroup;
